import React from 'react';
import { motion } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { Project } from '../../types';

interface ProjectModalProps {
  project: Project;
  onClose: () => void;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ project, onClose }) => {
  return (
    <div className='fixed inset-0 z-[60] flex items-center justify-center p-4'>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className='absolute inset-0 bg-carbon/60 backdrop-blur-sm'
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className='modal-scroll-container relative w-full max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain bg-paper rounded-3xl border border-line shadow-2xl'
        onWheel={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='sticky top-0 bg-paper/95 backdrop-blur-md p-6 md:p-8 border-b border-line flex justify-between items-start z-10'>
          <div>
            <div className='label-mono text-gray2 mb-2'>
              <span className='text-accent'>[</span> Case study{' '}
              <span className='text-accent'>]</span>
            </div>
            <h2 className='font-medium tracking-tight text-2xl text-ink'>{project.title}</h2>
            <p className='text-sm text-gray1 mt-1'>{project.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            data-cursor='pointer'
            className='w-10 h-10 shrink-0 rounded-full border border-line flex items-center justify-center text-ink hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300'
            aria-label='Close'
          >
            <X size={18} />
          </button>
        </div>

        <div className='p-6 md:p-8 space-y-10'>
          <div>
            <div className='label-mono text-gray2 mb-3'>Overview</div>
            <p className='text-gray1 leading-relaxed'>{project.details.overview}</p>
          </div>

          <div>
            <div className='label-mono text-gray2 mb-4'>Key features</div>
            <ul className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              {project.details.features.map((feature, i) => (
                <li key={i} className='flex items-start gap-2.5 text-sm text-gray1'>
                  <Check size={15} className='text-accent mt-0.5 shrink-0' />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className='label-mono text-gray2 mb-4'>Technologies</div>
            <div className='flex flex-wrap gap-2'>
              {project.details.technologies.map(tech => (
                <span
                  key={tech}
                  className='label-mono !text-[10px] px-3 py-1.5 rounded-full bg-surface text-gray1'
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className='label-mono text-gray2 mb-4'>Impact</div>
            <div className='grid grid-cols-3 border-t border-l border-line'>
              {project.details.achievements.map((item, i) => (
                <div key={i} className='border-r border-b border-line p-4 sm:p-5 text-center'>
                  <div className='font-medium tracking-tight text-lg sm:text-2xl text-ink'>
                    {item.value}
                  </div>
                  <div className='label-mono !text-[9px] text-gray2 mt-1.5'>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className='label-mono text-gray2 mb-3'>Challenges & solutions</div>
            <p className='text-gray1 leading-relaxed'>{project.details.challenges}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProjectModal;
